import { isValidElement, useEffect, useMemo } from 'react';
import { App } from 'antd';
import {
    CheckCircleBoldIcon,
    InfoCircleBoldIcon,
    DangerTriangleBoldIcon,
    CloseCircleBoldIcon,
} from '@solar-icons/react';
import './Message.module.css';

const TYPE_ICONS = {
    success: <CheckCircleBoldIcon color="var(--color-feedback-success-text)" />,
    info: <InfoCircleBoldIcon color="var(--color-primary-700)" />,
    warning: <DangerTriangleBoldIcon color="var(--color-feedback-warning-text)" />,
    error: <CloseCircleBoldIcon color="var(--color-feedback-error-text)" />,
};

// Same backgrounds as vector's Alert component, for visual consistency between the two.
const TYPE_BACKGROUNDS = {
    success: 'var(--color-feedback-success-bg)',
    info: 'var(--color-primary-100)',
    warning: 'var(--color-feedback-warning-bg)',
    error: 'var(--color-feedback-error-bg)',
};

// antd hardcodes role="alert" (assertive) for every type. Routine confirmations shouldn't
// interrupt a screen reader mid-sentence, so only warning/error keep the assertive role.
const TYPE_ROLES = {
    success: 'status',
    info: 'status',
    warning: 'alert',
    error: 'alert',
};

const isConfigArg = arg => arg !== null && typeof arg === 'object' && !isValidElement(arg) && 'content' in arg;

// Mirrors antd's own success/info/warning/error overload (content|config, duration|onClose, onClose)
// so callers can keep using either call shape, with our icon layered in as the default.
const buildTypeOpen = (open, type) => (jointContent, duration, onClose) => {
    const config = isConfigArg(jointContent) ? jointContent : { content: jointContent };
    const mergedDuration = typeof duration === 'function' ? undefined : duration;
    const mergedOnClose = typeof duration === 'function' ? duration : onClose;

    return open({
        onClose: mergedOnClose,
        duration: mergedDuration,
        icon: TYPE_ICONS[type],
        role: TYPE_ROLES[type],
        style: { background: TYPE_BACKGROUNDS[type] },
        ...config,
        type,
    });
};

const buildMessageApi = antdMessage => ({
    ...antdMessage,
    success: buildTypeOpen(antdMessage.open, 'success'),
    info: buildTypeOpen(antdMessage.open, 'info'),
    warning: buildTypeOpen(antdMessage.open, 'warning'),
    error: buildTypeOpen(antdMessage.open, 'error'),
});

let activeApi = null;

const callActive = method => (...args) => {
    if (!activeApi) {
        console.warn(`vector message.${method}() called before ConfigProvider mounted — message was dropped`);
        return undefined;
    }
    return activeApi[method](...args);
};

// Module-level singleton so callers can `import { message } from 'vector'` with no hook of their
// own — MessageBridge below is what keeps it pointed at the live, themed antd instance.
const message = {
    /** @param {Object} config - Same shape as antd's own `message.open` config. */
    open: callActive('open'),
    /**
     * @param {string|Object} content - Message text, or a config object (`{ content, duration,
     *   onClose, closable, ... }`) — same dual call shape as antd's own `message.success`.
     * @param {number|Function} [duration] - Seconds before auto-dismiss (0 = never), or an
     *   `onClose` callback in the shorthand call form.
     * @param {Function} [onClose] - Called when the message closes.
     */
    success: callActive('success'),
    /** @see message.success - same call shape. */
    info: callActive('info'),
    /** @see message.success - same call shape. */
    warning: callActive('warning'),
    /** @see message.success - same call shape. */
    error: callActive('error'),
    /** @param {string|Object} content - Same call shape as `message.success`. */
    loading: callActive('loading'),
    /** @param {string|number} [key] - Closes one message by key, or every message if omitted. */
    destroy: callActive('destroy'),
};

export const MessageBridge = () => {
    const { message: antdMessage } = App.useApp();
    const api = useMemo(() => buildMessageApi(antdMessage), [antdMessage]);

    useEffect(() => {
        activeApi = api;
        return () => {
            activeApi = null;
        };
    }, [api]);

    return null;
};

export default message;
